// E1 Phase 8 — active workspace store (billing attribution).
//
// Holds the session's selected workspace and the user's workspace list + per-workspace
// credit reports. The selected workspace id drives TWO things:
//   1. recordings uploads are tagged with it (grant-validated server-side — works
//      today, no mount needed);
//   2. a SetActiveWorkspace WS switch is sent so chat/voice LLM spend also attributes
//      to the workspace — this part activates once the workspace is MOUNTED (the mount FE
//      ships with the wider E1 client work; until then the switch is a safe no-op
//      server-side and only the recordings tagging is live).
//
// Session-tied (account-bound) → persisted under the `lvdr_session_` prefix so the
// hardLogout flow wipes it on lock/logout (CLAUDE.md persisted-store convention).
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';

import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import type {
  MountWorkspaceResponse,
  SetActiveWorkspaceResponse,
  UnmountWorkspaceResponse,
  WorkspaceChangeEvent,
} from '@/lib/protobuf/gen/workspace/workspace_pb';
import { wsManager } from '@/lib/websocket';
import { useAuthStore } from '@/stores/authStore';
import { useTelegramStore } from '@/features/inbox/telegramStore';

import {
  createWorkspaceE2E,
  getMyKeyMaterial,
  getWorkspaceUsage,
  listMyDevices,
  listWorkspaces,
  sendMountWorkspace,
  sendRekeyWorkspace,
  sendUnmountWorkspace,
  sendSetActiveWorkspace,
  setWorkspaceBudget,
  type Workspace,
  type WorkspaceUsage,
} from './api';
import {
  cacheWorkspaceDEK,
  clearAllWorkspaceDEKs,
  clearWorkspaceDEK,
  generateWrappedDEKForDevices,
  unwrapMyDEK,
} from './workspaceCrypto';
import { resetAlwaysOnMount, useAlwaysOnMountStore } from './alwaysOnMountStore';
import { useAlwaysOnSpaceStore } from './alwayson/alwaysOnSpaceStore';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * AON-03: ids of the user's always-on space. It stays invisible until AON-08
 * owns its UI, so it is kept out of `workspaces` (every list — switcher,
 * settings, transfer targets, filing target — derives from that) and a mount
 * response for it writes nothing. A keyed set, not state: nothing renders it.
 * Filled by loadWorkspaces (kind === 'always_on') and by the auto-mount before
 * it sends the mount, since that response can land before the list does.
 */
const hiddenAlwaysOnIds = new Set<string>();

/** Hide a workspace id from the store's lists and mount bookkeeping (AON-03). */
export function hideAlwaysOnWorkspace(workspaceId: string): void {
  if (workspaceId) hiddenAlwaysOnIds.add(workspaceId);
}

/**
 * NOTES-16 (D59): what happened to this tab's access to a workspace. The workspace
 * note save outbox listens, so its drafts follow access: `mounted` lets them
 * persist and resend, `unmount` purges them, `rekey` drops the disk copy and holds
 * the live entries until the next `mounted`. A failed mount emits nothing.
 */
export type WorkspaceAccessEvent = 'mounted' | 'unmount' | 'rekey';

type WorkspaceAccessListener = (workspaceId: string, ev: WorkspaceAccessEvent) => void;

const accessListeners = new Set<WorkspaceAccessListener>();

/** Subscribe to workspace access events; returns the unsubscribe. */
export function registerWorkspaceAccessListener(fn: WorkspaceAccessListener): () => void {
  accessListeners.add(fn);
  return () => {
    accessListeners.delete(fn);
  };
}

function emitAccess(workspaceId: string, ev: WorkspaceAccessEvent): void {
  for (const fn of [...accessListeners]) {
    try {
      fn(workspaceId, ev);
    } catch (e) {
      console.error('[workspaces] access listener failed', ev, e);
    }
  }
}

/** A mounted workspace's access level, as reported by the mount handshake. */
export type WorkspaceAccessLevel = 'read' | 'read_write';

interface WorkspaceState {
  workspaces: Workspace[];
  /** The workspace the session is working in; null = personal (org-pool) context. */
  activeWorkspaceId: string | null;
  loadState: LoadState;
  /** Per-workspace credit report cache, keyed by workspace id. */
  usageById: Record<string, WorkspaceUsage>;
  /**
   * Workspaces MOUNTED in THIS session, keyed by id → access level. Ephemeral
   * (the DEK lives in the session keyring, so this never persists — it is
   * rebuilt from mount handshakes after every reconnect). Drives the transfer
   * target picker, which may only target a mounted, writable island.
   */
  mountedById: Record<string, WorkspaceAccessLevel>;
  /**
   * MEM-09m: per mounted workspace, whether the mount response said the island
   * is held by another backend (`peer_owned`; writes are refused there). Set and
   * deleted with `mountedById`; the server's value, absent ⇒ false.
   */
  peerOwnedById: Record<string, boolean>;
  /**
   * Per-workspace mount progress, keyed by id (NEVER a single module slot — two
   * concurrent mounts must not clobber each other; CLAUDE.md FE race rule). A
   * value of 'error' means the last mount attempt failed (timeout / bad key).
   */
  mountStateById: Record<string, 'mounting' | 'error'>;

  actions: {
    loadWorkspaces: () => Promise<void>;
    /** Create a workspace with a CLIENT-generated WS-DEK (E1 Phase 13), mount it, and select it. */
    createWorkspace: (name: string, orgId: string) => Promise<Workspace>;
    /**
     * Mount a workspace: fetch the caller's key-material, unwrap the WS-DEK with
     * this device's key, send MountWorkspaceRequest, then set it active. The
     * plaintext DEK is cached in-memory (workspaceCrypto) for grant/escrow/re-key.
     */
    mount: (workspaceId: string) => Promise<void>;
    /** Unmount a workspace and drop its cached DEK. */
    unmount: (workspaceId: string) => Promise<void>;
    /** Trigger a revocation re-key (requires mounted), then re-mount with the new DEK. */
    rekey: (workspaceId: string) => Promise<void>;
    /** Select the active workspace (null = clear). Optimistic; sends the WS switch. */
    setActive: (workspaceId: string | null) => void;
    loadUsage: (workspaceId: string) => Promise<void>;
    setBudget: (workspaceId: string, budgetCredits: number | null) => Promise<void>;
    handleSetActiveResponse: (env: MessageEnvelope) => void;
    handleMountResponse: (env: MessageEnvelope) => void;
    handleUnmountResponse: (env: MessageEnvelope) => void;
    handleWorkspaceChangeEvent: (env: MessageEnvelope) => void;
  };
}

/** Resolve the current authenticated user id (for device-key lookup). */
function currentUserId(): string {
  return useAuthStore.getState().user?.id ?? '';
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools(
    subscribeWithSelector(
      persist(
        immer((set, get) => ({
          workspaces: [],
          activeWorkspaceId: null,
          loadState: 'idle' as LoadState,
          usageById: {},
          mountedById: {},
          peerOwnedById: {},
          mountStateById: {},

          actions: {
            loadWorkspaces: async () => {
              set((s) => {
                s.loadState = 'loading';
              });
              try {
                const ws = (await listWorkspaces()).filter((w) => {
                  if (w.kind === 'always_on') {
                    hiddenAlwaysOnIds.add(w.id);
                    return false;
                  }
                  return true;
                });
                set((s) => {
                  s.workspaces = ws;
                  s.loadState = 'ready';
                  // Drop a stale active selection if the workspace no longer exists.
                  if (s.activeWorkspaceId && !ws.some((w) => w.id === s.activeWorkspaceId)) {
                    s.activeWorkspaceId = null;
                  }
                });
              } catch {
                set((s) => {
                  s.loadState = 'error';
                });
              }
            },

            createWorkspace: async (name, orgId) => {
              // CLIENT-SIDE-E2E create (E1 Phase 13): generate the WS-DEK on this
              // device and wrap it to all of the owner's devices. The server never
              // sees the plaintext DEK.
              const devices = await listMyDevices();
              const { dek, wrapped } = await generateWrappedDEKForDevices(devices);
              let w: Workspace;
              try {
                w = await createWorkspaceE2E(name, orgId, wrapped);
              } catch (e) {
                dek.fill(0); // create failed — zero the plaintext DEK we won't use
                throw e;
              }
              // We already hold the plaintext DEK — cache it + mount directly
              // (no need to re-fetch/re-unwrap), set active, then refresh the list.
              cacheWorkspaceDEK(w.id, dek);
              try {
                const resp = await sendMountWorkspace(w.id, dek);
                const level: WorkspaceAccessLevel =
                  resp.accessLevel === 'read_write' ? 'read_write' : 'read';
                set((s) => {
                  s.mountedById[w.id] = level;
                  s.peerOwnedById[w.id] = resp.peerOwned === true;
                });
                get().actions.setActive(w.id);
              } catch {
                // Created but couldn't auto-mount — surface an error state so the
                // list row shows a retry affordance (don't keep a dangling DEK).
                clearWorkspaceDEK(w.id);
                set((s) => {
                  s.mountStateById[w.id] = 'error';
                });
              }
              await get().actions.loadWorkspaces();
              return w;
            },

            mount: async (workspaceId) => {
              // Already mounted → no-op (idempotent; avoids a redundant round-trip).
              if (get().mountedById[workspaceId]) return;
              set((s) => {
                s.mountStateById[workspaceId] = 'mounting';
              });
              try {
                const rows = await getMyKeyMaterial(workspaceId);
                const dek = await unwrapMyDEK(rows, currentUserId());
                cacheWorkspaceDEK(workspaceId, dek);
                const resp = await sendMountWorkspace(workspaceId, dek);
                const level: WorkspaceAccessLevel =
                  resp.accessLevel === 'read_write' ? 'read_write' : 'read';
                set((s) => {
                  s.mountedById[workspaceId] = level;
                  s.peerOwnedById[workspaceId] = resp.peerOwned === true;
                  delete s.mountStateById[workspaceId];
                });
                emitAccess(workspaceId, 'mounted');
                get().actions.setActive(workspaceId);
              } catch (e) {
                clearWorkspaceDEK(workspaceId);
                set((s) => {
                  s.mountStateById[workspaceId] = 'error';
                });
                throw e;
              }
            },

            unmount: async (workspaceId) => {
              try {
                await sendUnmountWorkspace(workspaceId);
              } finally {
                // Always drop local state even if the server round-trip fails — the
                // session ending is what matters; a stale mountedById is worse.
                clearWorkspaceDEK(workspaceId);
                set((s) => {
                  delete s.mountedById[workspaceId];
                  delete s.peerOwnedById[workspaceId];
                  delete s.mountStateById[workspaceId];
                  if (s.activeWorkspaceId === workspaceId) s.activeWorkspaceId = null;
                });
                emitAccess(workspaceId, 'unmount');
              }
            },

            rekey: async (workspaceId) => {
              // Re-key requires the workspace mounted (the server reads the OLD DEK
              // from the session keyring). The server zeroes the cached DEK on
              // success, so we must re-fetch + unwrap the NEW key-material and
              // re-mount.
              await sendRekeyWorkspace(workspaceId);
              clearWorkspaceDEK(workspaceId);
              set((s) => {
                delete s.mountedById[workspaceId];
                delete s.peerOwnedById[workspaceId];
              });
              emitAccess(workspaceId, 'rekey');
              await get().actions.mount(workspaceId);
            },

            setActive: (workspaceId) => {
              set((s) => {
                s.activeWorkspaceId = workspaceId;
              });
              // Empty string clears the server-side active context.
              sendSetActiveWorkspace(workspaceId ?? '');
            },

            loadUsage: async (workspaceId) => {
              // The usage endpoint is owner/org-admin-scoped (billing data) — a
              // grantee's fetch is a guaranteed 403. Skip it client-side when we
              // can already see the caller doesn't own the workspace; the switcher's
              // spend/budget line simply doesn't render without usageById data.
              const ws = get().workspaces.find((w) => w.id === workspaceId);
              if (ws && ws.owner_user_id !== currentUserId()) return;
              try {
                const usage = await getWorkspaceUsage(workspaceId);
                set((s) => {
                  s.usageById[workspaceId] = usage;
                });
              } catch {
                // best-effort; the report panel renders an empty/last-known state
              }
            },

            setBudget: async (workspaceId, budgetCredits) => {
              const usage = await setWorkspaceBudget(workspaceId, budgetCredits);
              set((s) => {
                s.usageById[workspaceId] = usage;
                const w = s.workspaces.find((x) => x.id === workspaceId);
                if (w) {
                  if (budgetCredits === null) delete w.budget_credits;
                  else w.budget_credits = budgetCredits;
                }
              });
            },

            // Reconcile the server's view of the active workspace. The server only
            // honours a workspace the session has MOUNTED; if it answers with a
            // different/empty workspace than we optimistically set, trust the server.
            handleSetActiveResponse: (env: MessageEnvelope) => {
              if (env.payload.case !== 'setActiveWorkspaceResponse') return;
              const value = env.payload.value as SetActiveWorkspaceResponse;
              set((s) => {
                s.activeWorkspaceId = value.workspaceId || null;
              });
            },

            // Record a mount + its access level for this session. The transfer
            // picker reads this to offer only mounted, writable islands.
            handleMountResponse: (env: MessageEnvelope) => {
              if (env.payload.case !== 'mountWorkspaceResponse') return;
              const value = env.payload.value as MountWorkspaceResponse;
              if (!value.workspaceId) return;
              // AON-03: the silent always-on space auto-mount stays invisible.
              if (hiddenAlwaysOnIds.has(value.workspaceId)) return;
              const level: WorkspaceAccessLevel =
                value.accessLevel === 'read_write' ? 'read_write' : 'read';
              set((s) => {
                s.mountedById[value.workspaceId] = level;
                s.peerOwnedById[value.workspaceId] = value.peerOwned === true;
              });
              emitAccess(value.workspaceId, 'mounted');
            },

            handleUnmountResponse: (env: MessageEnvelope) => {
              if (env.payload.case !== 'unmountWorkspaceResponse') return;
              const value = env.payload.value as UnmountWorkspaceResponse;
              if (!value.workspaceId) return;
              clearWorkspaceDEK(value.workspaceId);
              set((s) => {
                delete s.mountedById[value.workspaceId];
                delete s.peerOwnedById[value.workspaceId];
              });
              emitAccess(value.workspaceId, 'unmount');
            },

            // A workspace-level change pushed from the owning backend. We only act
            // on "rekeyed" here: if ANOTHER member re-keyed a workspace this session
            // has mounted, our cached DEK is now stale (the server zeroed its copy).
            // Drop it and re-mount with the new key-material so the session keeps
            // working without a manual reload. (Note-level ops are handled by the
            // notes store's own subscriber.)
            handleWorkspaceChangeEvent: (env: MessageEnvelope) => {
              if (env.payload.case !== 'workspaceChangeEvent') return;
              const value = env.payload.value as WorkspaceChangeEvent;
              if (value.op !== 'rekeyed' || !value.workspaceId) return;
              if (!get().mountedById[value.workspaceId]) return; // not ours / not mounted
              clearWorkspaceDEK(value.workspaceId);
              set((s) => {
                delete s.mountedById[value.workspaceId];
                delete s.peerOwnedById[value.workspaceId];
              });
              emitAccess(value.workspaceId, 'rekey');
              // Re-mount with the rotated key; swallow errors (UI shows unmounted).
              void get().actions.mount(value.workspaceId).catch(() => undefined);
            },
          },
        })),
        {
          name: 'lvdr_session_active_workspace',
          partialize: (state) => ({ activeWorkspaceId: state.activeWorkspaceId }),
        },
      ),
    ),
    { name: 'WorkspaceStore' },
  ),
);

/** Convenience selector: the active workspace object, or undefined. */
export function useActiveWorkspace(): Workspace | undefined {
  return useWorkspaceStore((s) =>
    s.activeWorkspaceId ? s.workspaces.find((w) => w.id === s.activeWorkspaceId) : undefined,
  );
}

/**
 * The workspaces this session has mounted with WRITE access — the only valid
 * transfer targets (an unmounted or read-only island can't be written to). The
 * `loadWorkspaces` list supplies the names; `mountedById` supplies the mount +
 * access gate. Stable-sorted by name for a deterministic picker.
 */
export function useWritableMountedWorkspaces(): Workspace[] {
  // NB: this selector derives a NEW array (.filter().slice().sort()). Under
  // zustand v5 the store hook is a bare useSyncExternalStore with no equality
  // check, so an unwrapped derived-array selector reports the snapshot as
  // changed on every render and React loops to the update-depth limit
  // ("Maximum update depth exceeded", React #185). useShallow holds the prior
  // reference and returns it when the contents are unchanged, keeping the
  // snapshot stable. (Same pattern as ResearchList's selectFilteredResearch.)
  return useWorkspaceStore(
    useShallow((s) =>
      s.workspaces
        .filter((w) => s.mountedById[w.id] === 'read_write')
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    ),
  );
}

/**
 * Registers the workspace WS response handlers (active-workspace switch + mount /
 * unmount tracking). Call once on app init (alongside the other
 * register*Handlers in AppShell). Returns a combined unsubscribe.
 */
export function registerWorkspaceHandlers(): () => void {
  const { actions } = useWorkspaceStore.getState();
  const offs = [
    wsManager.onMessage('setActiveWorkspaceResponse', actions.handleSetActiveResponse),
    wsManager.onMessage('mountWorkspaceResponse', actions.handleMountResponse),
    wsManager.onMessage('unmountWorkspaceResponse', actions.handleUnmountResponse),
    wsManager.onMessage('workspaceChangeEvent', actions.handleWorkspaceChangeEvent),
  ];
  return () => {
    for (const off of offs) off();
  };
}

/**
 * Drops every in-memory plaintext Workspace DEK + the mounted/active session
 * state. MUST be called when the crypto session is evicted (screen-lock /
 * session-kill) and on hardLogout — otherwise a backgrounded tab keeps plaintext
 * DEKs alive after the server evicted the session keyring (CLAUDE.md DEK-eviction
 * convention). Idempotent.
 */
export function clearWorkspaceSessionKeys(): void {
  // NOTES-16: every workspace this tab could reach loses access here, the
  // always-on space included (it is never in mountedById, AON-03). Read before
  // the clears; the `unmount` events go out after them.
  const closing = new Set(Object.keys(useWorkspaceStore.getState().mountedById));
  const alwaysOn = useAlwaysOnMountStore.getState();
  if (alwaysOn.workspaceId) closing.add(alwaysOn.workspaceId);
  // A reconnect resets the always-on mount before it re-records it, so the id
  // may be absent there while its held saves are not: every known always-on id closes.
  for (const id of hiddenAlwaysOnIds) closing.add(id);
  clearAllWorkspaceDEKs();
  hiddenAlwaysOnIds.clear();
  // AON-06 (plan-review rev2 I2): the always-on mount state goes with the keys,
  // so the next user in this tab never sees the previous user's space.
  resetAlwaysOnMount();
  // AON-08: and the always-on space status (and its user binding) with it.
  useAlwaysOnSpaceStore.getState().actions.reset();
  // INBOX-13a: the in-memory /start deep link is a bearer link token; it goes with the session.
  useTelegramStore.getState().actions.reset();
  useWorkspaceStore.setState((s) => ({
    ...s,
    mountedById: {},
    peerOwnedById: {},
    mountStateById: {},
    activeWorkspaceId: null,
  }));
  for (const id of closing) emitAccess(id, 'unmount');
}
